/**
 * HeroBillsChart — card "hero" della Home con il chart consumi.
 *
 * Replica web del componente mobile
 * `enermed-app/src/screens/home/components/v2/HeroBills.js`:
 *   - sfondo navy con barre bianche/cyan (no Recharts, solo flex+div per
 *     coerenza visiva con la card "hero" mobile)
 *   - prende le ultime 10 bollette e ne mostra il `consumo` (kWh)
 *   - click su una barra → header sopra il chart mostra
 *     "Consumi di Marzo · 312 kWh"
 *   - stats riga in basso: Media (kWh) e trend % vs precedente
 *     (verde se in calo, ambra se in salita)
 */

import { useState } from 'react'
import { parseBackendDate } from '@/lib/dates'
import type { Bolletta } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'

const MAX_BILLS = 10

type HeroBillsChartProps = {
  invoices: Bolletta[] | undefined
  addressLabel?: string | null
}

export function HeroBillsChart({ invoices, addressLabel }: HeroBillsChartProps) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null)

  const lastN = (invoices ?? []).slice(0, MAX_BILLS).reverse()
  const consumi = lastN.map((b) => Number(b?.consumo) || 0)
  const maxConsumo = consumi.length > 0 ? Math.max(...consumi, 1) : 1

  const selectedBill = selectedIdx != null ? lastN[selectedIdx] : null
  const selectedValue = selectedBill ? Number(selectedBill.consumo) || 0 : null
  const selectedLabel = selectedBill ? labelFor(selectedBill) : ''

  const media =
    consumi.length > 0
      ? Math.round(consumi.reduce((a, b) => a + b, 0) / consumi.length)
      : 0

  let trendPct: number | null = null
  if (consumi.length >= 2) {
    const ultima = consumi[consumi.length - 1]
    const precedente = consumi[consumi.length - 2]
    if (precedente > 0) trendPct = ((ultima - precedente) / precedente) * 100
  }

  const trendColor =
    trendPct == null
      ? 'text-white/60'
      : trendPct <= 0
        ? 'text-emerald-400'
        : 'text-amber-400'

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-xl bg-primary p-6 text-white dark:bg-[#0F1E3D]">
      {/* Glow decorativo cyan in alto a destra per rompere il blocco navy */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full opacity-30 blur-3xl"
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--secondary) / 0.7), hsl(var(--secondary) / 0))',
        }}
      />

      <div className="relative flex flex-1 flex-col">
        {addressLabel && (
          <p className="truncate text-sm text-white/75">{addressLabel}</p>
        )}
        <h2 className="mt-0.5 text-lg font-bold">Consumi ultime bollette</h2>

        {consumi.length === 0 ? (
          <p className="mt-8 text-center text-sm text-white/70">
            Non ci sono ancora bollette da mostrare.
          </p>
        ) : (
          <>
            <div className="mt-5 min-h-[32px]">
              {selectedBill ? (
                <div className="flex items-baseline justify-between gap-2">
                  <p className="truncate text-xs text-white/75">
                    {selectedLabel
                      ? `Consumi di ${selectedLabel}`
                      : 'Bolletta'}
                  </p>
                  <p className="text-base font-bold text-secondary">
                    {(selectedValue ?? 0).toFixed(0)} kWh
                  </p>
                </div>
              ) : (
                <p className="text-xs text-white/60">
                  Ultime {consumi.length} bollette (kWh) · clicca una barra
                </p>
              )}
            </div>

            <div className="mt-3 flex h-[88px] items-end gap-1.5">
              {lastN.map((b, idx) => {
                const val = Number(b?.consumo) || 0
                const heightPct = (val / maxConsumo) * 100
                const isLast = idx === lastN.length - 1
                const isSelected = selectedIdx === idx
                const dimmed = selectedIdx != null && !isSelected
                return (
                  <button
                    key={(b?.codice ?? idx).toString()}
                    type="button"
                    onClick={() =>
                      setSelectedIdx(isSelected ? null : idx)
                    }
                    aria-label={`Bolletta ${labelFor(b) || idx + 1}, ${val.toFixed(0)} kWh`}
                    className="group relative h-full flex-1 rounded-sm bg-white/10 transition-opacity hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                  >
                    <span
                      className={cn(
                        'absolute bottom-0 left-0 right-0 rounded-sm transition-all',
                        isSelected || isLast ? 'bg-secondary' : 'bg-white/85',
                        dimmed && 'opacity-55',
                      )}
                      style={{ height: `${heightPct}%` }}
                    />
                  </button>
                )
              })}
            </div>

            <div className="mt-5 flex flex-wrap items-end justify-around gap-y-3 border-t border-white/15 pt-4">
              <Stat label="Media consumo per bolletta" value={`${media} kWh`} />
              {trendPct != null && (
                <Stat
                  label="Ultima bolletta vs precedente"
                  value={`${trendPct > 0 ? '+' : ''}${trendPct.toFixed(1)}%`}
                  valueClassName={trendColor}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  valueClassName,
}: {
  label: string
  value: string
  valueClassName?: string
}) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-xs text-white/70">{label}</span>
      <span className={cn('text-base font-bold', valueClassName ?? 'text-white')}>
        {value}
      </span>
    </div>
  )
}

/**
 * Etichetta del periodo della bolletta (per header su click).
 *  - dal/al stesso mese → "Marzo"
 *  - dal/al mesi diversi → "Febbraio - Marzo"
 *  - fallback su data_emissione (mese) o codice
 */
function labelFor(bill: Bolletta | undefined): string {
  if (!bill) return ''
  const dal = parseBackendDate(bill.dal)
  const al = parseBackendDate(bill.al)
  if (dal && al) {
    const dalMese = capitalize(dal.format('MMMM'))
    const alMese = capitalize(al.format('MMMM'))
    return dalMese === alMese ? dalMese : `${dalMese} - ${alMese}`
  }
  const em = parseBackendDate(bill.data_emissione)
  if (em) return capitalize(em.format('MMMM'))
  if (bill.codice) return `Bolletta ${bill.codice}`
  return ''
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}
